// Tier 2/3 section gestures — ported from claude/frosty-rosalind-246edb
// commit 308af3b. Wires user input on `.rh-section` ribbons to seek + loop
// toggle callbacks; orchestrator (main.ts) decides what to do with the ids.
//
// Tier 2 (always on):
//   - click           → onSeek(section.startSec) + 200ms .is-flash class
// Tier 3 (loop):
//   - dblclick        → onToggleLoop(sectionId)
//   - touch longpress → onToggleLoop(sectionId)         (mobile)
//   - Escape key      → onToggleLoop('')                (orchestrator clears)
//
// All handlers delegate from the host element via `closest('[data-section-id]')`
// so re-renders (renderSectionsBySec rebuilds ribbons every tick) don't
// invalidate listeners.

import type { ShareManifest } from '@bandway/share-types';

type Section = ShareManifest['sections'][number];

export interface SectionHandlersOpts {
  onSeek: (timeSec: number) => void;
  onToggleLoop: (sectionId: string) => void;
}

export interface SectionHandlersHandle {
  unmount(): void;
}

const LONGPRESS_MS = 500;
const FLASH_MS = 210;

export function mountSectionHandlers(
  host: HTMLElement,
  sections: Section[],
  opts: SectionHandlersOpts,
): SectionHandlersHandle {
  const byId = new Map(sections.map((s) => [s.id, s]));

  function flashSection(target: HTMLElement) {
    target.classList.add('is-flash');
    setTimeout(() => target.classList.remove('is-flash'), FLASH_MS);
  }

  function handleClick(ev: Event) {
    const target = (ev.target as HTMLElement | null)?.closest<HTMLElement>('[data-section-id]');
    if (!target) return;
    const sec = byId.get(target.dataset.sectionId!);
    if (!sec) return;
    opts.onSeek(sec.startSec);
    flashSection(target);
  }

  function handleDblClick(ev: Event) {
    const target = (ev.target as HTMLElement | null)?.closest<HTMLElement>('[data-section-id]');
    if (!target) return;
    opts.onToggleLoop(target.dataset.sectionId!);
  }

  let pressTimer: ReturnType<typeof setTimeout> | undefined;
  let pressTarget: HTMLElement | null = null;
  function handleTouchStart(ev: TouchEvent) {
    const target = (ev.target as HTMLElement | null)?.closest<HTMLElement>('[data-section-id]');
    if (!target) return;
    pressTarget = target;
    pressTimer = setTimeout(() => {
      pressTimer = undefined;
      if (pressTarget) opts.onToggleLoop(pressTarget.dataset.sectionId!);
    }, LONGPRESS_MS);
  }
  function handleTouchEnd() {
    if (pressTimer !== undefined) {
      clearTimeout(pressTimer);
      pressTimer = undefined;
    }
    pressTarget = null;
  }

  function handleKey(ev: KeyboardEvent) {
    if (ev.key === 'Escape') {
      opts.onToggleLoop(''); // empty signal = clear loop
    }
  }

  host.addEventListener('click', handleClick);
  host.addEventListener('dblclick', handleDblClick);
  host.addEventListener('touchstart', handleTouchStart, { passive: true });
  host.addEventListener('touchend', handleTouchEnd, { passive: true });
  host.addEventListener('touchmove', handleTouchEnd, { passive: true });
  host.addEventListener('touchcancel', handleTouchEnd, { passive: true });
  document.addEventListener('keydown', handleKey);

  return {
    unmount() {
      host.removeEventListener('click', handleClick);
      host.removeEventListener('dblclick', handleDblClick);
      host.removeEventListener('touchstart', handleTouchStart);
      host.removeEventListener('touchend', handleTouchEnd);
      host.removeEventListener('touchmove', handleTouchEnd);
      host.removeEventListener('touchcancel', handleTouchEnd);
      document.removeEventListener('keydown', handleKey);
      if (pressTimer !== undefined) clearTimeout(pressTimer);
    },
  };
}
